import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout } from '@/components';
import { FundingScreenSkeleton } from './FundingScreenSkeleton';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useFunding } from '@/hooks/admin/useFunding';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { FundingDataTable } from '@/components/admin/funding/FundingDataTable';
import { FundingMassActions } from '@/components/admin/funding/FundingMassActions';
import { IFundingTableRow } from '@/types/admin/funding';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { Button } from '@/components/common/admin/mobile/Button';
import { formatFundingCsvData, FUNDING_CSV_HEADERS } from '@/utils/admin/fundingUtils';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { useLazyDownloadFundingsQuery } from '@/store/api';
import { isRowDownloadable } from '@/utils/admin/fundingUtils';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type FundingScreenProps = StackScreenProps<AdminStackParamList, 'Funding'>;

const CSV_COLUMNS = FUNDING_CSV_HEADERS as (string | number)[];

export const FundingScreen: React.FC<FundingScreenProps> = ({
  navigation,
}) => {
  const [csvData, setCsvData] = useState<(string | number)[][]>([CSV_COLUMNS]);
  const { showApiError, showError } = useErrorToast();
  const adminUser = useAdminUser();
  const { layoutMode } = useResponsive();
  const styles = useStyles();
  const { admin, semantic } = useAdminColors();

  const {
    data,
    tableRows,
    propertyData,
    isLoading,
    error,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    selectedRows,
    updateRowSelection,
    clearRowSelection,
    selectAllRows,
    deselectAllRows,
    handleReprocessComplete,
    handleBulkReprocessComplete,
    handleDownloadComplete,
    refresh,
  } = useFunding();

  const [triggerDownload] = useLazyDownloadFundingsQuery();

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  // Format CSV data (matching admin-web logic exactly)
  const formatCsvData = useCallback((newData: IFundingTableRow[]) => {
    if (!newData || newData.length === 0) {
      setCsvData([CSV_COLUMNS]);
      return;
    }

    const rawData = {
      count: newData.length,
      rows: newData.map(row => ({
        property: row.property,
        merchantId: row.merchantId,
        fundingDate: row.fundingDate.toISOString(),
        lastStatus: row.lastStatus,
        fundingJournalData: row.funding_amount,
      }))
    };

    const formattedCsvData = formatFundingCsvData(rawData);
    setCsvData(formattedCsvData);
  }, []);

  // Handle funding selection (for future funding details implementation)
  const selectFundingDetails = useCallback((funding: IFundingTableRow) => {
    // TODO: Navigate to funding details screen or show side panel
    console.log('Selected funding:', funding);
    // navigation.navigate('FundingDetails', { fundingId: funding.id });
  }, []);

  // Handle full table export (header export button)
  const handleExportTable = useCallback(() => {
    if (Platform.OS === 'web') {
      const filename = `resio-funding-${new Date().toJSON().slice(0, 10)}`;
      try {
        downloadCsvFrom2DArray(csvData, filename);
      } catch (e) {
        console.error('Failed to download CSV', e);
      }
    } else {
      // Mobile platforms: could share file or show a toast; for now, no-op
      console.log('CSV export is available on web only.');
    }
  }, [csvData]);

  // Handle bulk download via server (mass actions panel)
  const handleBulkDownload = useCallback(async () => {
    if (Platform.OS !== 'web') {
      console.log('Bulk download is available on web only.');
      return;
    }
    if (!adminUser.selectedOrganizationId) {
      showApiError(new Error('No organization selected'), 'No organization selected');
      return;
    }
    if (!selectedRows || selectedRows.length === 0) {
      console.warn('No rows selected for bulk download');
      return;
    }

    try {
      // Pre-validate: only include rows that have downloadable journal data
      const downloadable = selectedRows.filter(isRowDownloadable);
      if (downloadable.length === 0) {
        showError('Selected fundings have no data to download.');
        return;
      }
      const ids = downloadable.map((r) => r.id).sort((a, b) => a - b);
      const blob = await triggerDownload({
        ids,
        organizationId: adminUser.selectedOrganizationId,
      }).unwrap();

      const firstId = ids[0];
      const lastId = ids[ids.length - 1];
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `fundings_${firstId}_to_${lastId}_${dateStr}.csv`;

      const g: any = globalThis as any;
      const url = g.URL?.createObjectURL(blob as unknown as Blob);
      if (url && g.document) {
        const link = g.document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        g.document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          g.URL?.revokeObjectURL(url);
          link.remove();
        }, 100);
      }
    } catch (e) {
      console.error('Failed to download funding CSV (bulk):', e);
      showApiError(e as Error, 'Failed to download. Please try again.');
    }
  }, [adminUser.selectedOrganizationId, selectedRows, triggerDownload, showApiError]);

  // Keep csvData synced with selected rows (mirror admin-web behavior)
  useEffect(() => {
    if (selectedRows.length > 0) {
      formatCsvData(selectedRows);
    } else if (tableRows && tableRows.length > 0) {
      formatCsvData(tableRows);
    } else {
      setCsvData([CSV_COLUMNS]);
    }
  }, [selectedRows, tableRows, formatCsvData]);

  // Determine if we should show data or empty state
  const hasData = data.count > 0;
  
  // Show error state if we have a critical error and no data
  const showErrorState = enhancedError && !hasData && !isLoading;
  
  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData && !showErrorState;

  // Show mass actions panel when rows are selected
  const showMassActions = selectedRows.length > 0;

  // Render error state component
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text style={styles.errorTitle}>Unable to Load Funding</Text>
      <Text style={styles.errorMessage}>
        {enhancedError?.userFriendlyMessage || 'Something went wrong while loading the funding list.'}
      </Text>

      {retryCount > 0 && (
        <Text style={styles.retryInfo}>Retry attempts: {retryCount}</Text>
      )}

      {canRetry && (
        <Button title="Try Again" onPress={manualRetry} variant="primary" />
      )}
    </View>
  );

  // Render property count info (matching admin-web header)
  const renderPropertyInfo = () => {
    if (!propertyData || propertyData.length === 0) return null;

    return (
      <View style={styles.propertyInfoContainer}>
        <SVG_COMPONENTS.NEW_PROPERTY width={16} height={16} color={admin.dataCell.textMuted} />
        <Text style={styles.propertyInfoText}>{propertyData.length} Properties</Text>
      </View>
    );
  };



  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="Funding">
        <DesktopAdminLayout>
          <DesktopPageHeader
            title="Funding"
            subtitle=""
            showExportButton={hasData}
            onExportPress={handleExportTable}
            exportButtonText="Export Table"
          >
            {hasData && renderPropertyInfo()}
          </DesktopPageHeader>
          {showLoadingState ? (
            <FundingScreenSkeleton />
          ) : showErrorState ? (
            renderErrorState()
          ) : (
            <DataTableProvider tableKey="admin:funding" defaults={{ paginationModel }}>
              <View style={styles.dataContainer}>
                <FundingDataTable
                  data={data}
                  rows={tableRows}
                  isLoading={isLoading}
                  onFundingSelect={selectFundingDetails}
                  updateCsvData={formatCsvData}
                  propertyData={propertyData}
                  showSearch={false}
                  selectedRows={selectedRows}
                  onRowSelectionChange={updateRowSelection}
                  onReprocessComplete={handleReprocessComplete}
                  onSelectAll={selectAllRows}
                  onDeselectAll={deselectAllRows}
                />
              </View>
            </DataTableProvider>
          )}
          {showMassActions && (
            <FundingMassActions
              selectedRows={selectedRows}
              onClose={clearRowSelection}
              onReprocessComplete={handleBulkReprocessComplete}
              onDownload={handleBulkDownload}
            />
          )}
        </DesktopAdminLayout>
      </AdminErrorBoundary>
    );
  }

  // Mobile layout (existing)
  return (
    <AdminErrorBoundary context="Funding">
      <BaseAdminScreen
        screenName="Funding"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Funding"
          emptyStateType="funding"
          showAddButton={false}
          hasContent={true}
          disableScrollView={true}
        >
          {hasData && renderPropertyInfo()}
          {showLoadingState ? (
            <FundingScreenSkeleton />
          ) : showErrorState ? (
            renderErrorState()
          ) : (
            <DataTableProvider tableKey="admin:funding" defaults={{ paginationModel }}>
              <View style={styles.dataContainer}>
                <FundingDataTable
                  data={data}
                  rows={tableRows}
                  isLoading={isLoading}
                  onFundingSelect={selectFundingDetails}
                  updateCsvData={formatCsvData}
                  propertyData={propertyData}
                  showSearch={false}
                  selectedRows={selectedRows}
                  onRowSelectionChange={updateRowSelection}
                  onReprocessComplete={handleReprocessComplete}
                  onSelectAll={selectAllRows}
                  onDeselectAll={deselectAllRows}
                />
              </View>
            </DataTableProvider>
          )}
        </AdminEmptyScreenLayout>

        {showMassActions && (
          <FundingMassActions
            selectedRows={selectedRows}
            onClose={clearRowSelection}
            onReprocessComplete={handleBulkReprocessComplete}
          />
        )}
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      baseScreenContent: {
        paddingHorizontal: 0,
        paddingTop: 0,
        paddingBottom: 0,
        flexGrow: 1,
      },
      dataContainer: {
        flex: 1,
        width: '100%',
      },
      errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      },
      errorTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text.primary,
        marginTop: 16,
        marginBottom: 8,
        textAlign: 'center',
      },
      errorMessage: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
        marginBottom: 16,
        lineHeight: 20,
        maxWidth: 300,
      },
      retryInfo: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
        marginBottom: 16,
        fontStyle: 'italic',
      },
      propertyInfoContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: components.admin.formInput.bg,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: components.admin.dataTable.border,
        gap: 8,
      },
      propertyInfoText: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
        fontWeight: '500',
      },
    }),
  );